import find from 'lodash/find';
import reduce from 'lodash/reduce';
import zipObject from 'lodash/zipObject';

const TYPES = {
  STRING: 'string',
  BOOL: 'bool',
  INT: 'int'
};

function withTypes(obj, mappings) {
  return reduce(
    obj,
    function(acc, val, key) {
      if (val === '') {
        acc[key] = null;
      } else if (mappings[key] === TYPES.STRING) {
        acc[key] = val.toString();
      } else if (mappings[key] === TYPES.BOOL) {
        acc[key] = val === 'true';
      } else if (mappings[key] === TYPES.INT) {
        acc[key] = parseInt(val, 10);
      } else {
        acc[key] = val;
      }
      return acc;
    },
    {}
  );
}

/**
 * Expand seat data
 *
 * Tessitura provides a condensed seat data feed
 * and it is quicker to request this and expand it
 * client-side than it is to request the full feed.
 *
 * Data is returned as a comma separated strings under `.D`
 *
 * Empty values are included so the array length is always known,
 * which makes it safe to map keys like this.
 *
 * @param seatData - Tessitura seat data
 * @return {Object} - Expanded seat data
 */
export function expandSeatData(seatData, mappings) {
  mappings = mappings || {
    section: TYPES.INT,
    seat_row: TYPES.STRING,
    seat_num: TYPES.INT,
    seat_status: TYPES.INT,
    seat_no: TYPES.INT,
    zone_no: TYPES.INT,
    ac_no: TYPES.INT,
    seat_type: TYPES.INT,
    logical_seat_row: TYPES.INT,
    logical_seat_num: TYPES.INT,
    xpos: TYPES.INT,
    ypos: TYPES.INT,
    is_seat: TYPES.BOOL,
    aisle_ind: TYPES.STRING,
    no_stairs_ind: TYPES.STRING,
    display_letter: TYPES.STRING,
    screen_no: TYPES.INT,
    hc_no: TYPES.INT,
  };

  return seatData.map(function(result) {
    let zipped = zipObject(Object.keys(mappings), result.D.split(','));
    return withTypes(zipped, mappings);
  });
}

export function mergeSeatStatus(seatResults, seatStatus) {
  return Object.keys(seatResults).reduce(function(acc, curr) {
    let seat = seatResults[curr];
    seat.seat_status = find(seatStatus, function(item) {
      return parseInt(item.id, 10) === seat.seat_status;
    });
    return seatResults;
  }, {});
}

export function mergeSections(seatResults, sectionsResults) {
  return Object.keys(seatResults).reduce(function(acc, curr) {
    let seat = seatResults[curr];
    let section = find(sectionsResults, function(item) {
      return parseInt(item.section, 10) === seat.section;
    });
    if (section && section.section_desc) {
      seat.section_desc = section.section_desc;
    }
    if (section && section.short_desc) {
      seat.short_desc = section.short_desc;
    }

    return seatResults;
  }, {});
}

export function mergeScreenData(seatResults, screenResults) {
  return Object.keys(seatResults).reduce(function(acc, curr) {
    let seat = seatResults[curr];
    seat.screen = find(screenResults, function(item) {
      return parseInt(item.screen_no, 10) === seat.screen_no;
    });
    return seatResults;
  }, {});
}

export function mergeZoneData(seatResults, zonesResults) {
  return Object.keys(seatResults).reduce(function(acc, curr) {
    let seat = seatResults[curr];
    let zone = find(zonesResults, function(item) {
      return parseInt(item.zone_no, 10) === seat.zone_no;
    });

    return seatResults;
  }, {});
}

export function enhanceSeatData(
  seatResults,
  seatStatusResults,
  sectionsResults,
  screenResults,
) {
  let seats = expandSeatData(seatResults);
  seats = mergeSeatStatus(seats, seatStatusResults);
  seats = mergeSections(seats, sectionsResults);
  seats = mergeScreenData(seats, screenResults);
  return seats;
}

export function setScreenZoneData(seatsWithZones) {
  return reduce(seatsWithZones, function(acc, { Seat }) {
    const screenNo = parseInt(Seat.screen_no, 10);

    if (!screenNo) {
      return acc;
    }

    if (screenNo in acc === false) {
      acc[screenNo] = {
        zones: [],
        avail: 0,
      };
    }

    const zoneNo = parseInt(Seat.zone_no, 10);

    if (acc[Seat.screen_no]['zones'].indexOf(zoneNo) === -1) {
      acc[Seat.screen_no]['zones'].push(zoneNo);
    }

    return acc;
  }, {});
}

export default {
  TYPES,
  withTypes,
  expandSeatData,
  mergeSeatStatus,
  mergeScreenData,
  enhanceSeatData,
  setScreenZoneData
};



// WEBPACK FOOTER //
// ./src/javascripts/lib/common/transforms.js